import React, { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import secureApiClient from '../utils/secureApiClient';

const ALLOWED_ACCEPT = 'application/pdf,image/jpeg,image/png';

const CATEGORIES = [
  { id: 'roi', label: 'Release of Information (ROI)' },
  { id: 'privacy-practices', label: 'Privacy Practices' },
  { id: 'informed-consent', label: 'Informed Consent' },
];

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (ts) => {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FormTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);
  const uploadCategoryRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'formTemplates'), orderBy('uploadedAt', 'desc')),
      snap => {
        setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => !d.deleted));
        setLoading(false);
      },
      () => { setTemplates([]); setLoading(false); }
    );
    return () => unsub();
  }, []);

  const triggerUpload = (categoryId) => {
    uploadCategoryRef.current = categoryId;
    fileInputRef.current?.click();
  };

  const handleUpload = async (file) => {
    const category = uploadCategoryRef.current;
    setError('');
    setUploadingCategory(category);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.uploadFormTemplate,
        {
          method: 'POST',
          headers: {
            'Content-Type': file.type,
            'X-File-Name': encodeURIComponent(file.name),
            'X-Template-Category': category,
          },
          body: file,
        }
      );
    } catch (err) {
      setError(err.message || 'Failed to upload template.');
    } finally {
      setUploadingCategory(null);
      uploadCategoryRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openTemplate = async (docId, action) => {
    setError('');
    try {
      const data = await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.getFormTemplateUrl,
        { method: 'POST', body: JSON.stringify({ docId, action }) }
      );
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || 'Failed to open template.');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this template?')) return;
    setError('');
    setDeletingId(docId);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.deleteFormTemplate,
        { method: 'POST', body: JSON.stringify({ docId }) }
      );
    } catch (err) {
      setError(err.message || 'Failed to delete template.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Clinical Forms</h1>
      <p className="text-gray-500 mb-6">Blank forms for staff to print or send to clients.</p>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_ACCEPT}
        className="hidden"
        onChange={e => e.target.files[0] && handleUpload(e.target.files[0])}
      />

      {error && (
        <div className="mb-6 px-4 py-2 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-8 text-sm">Loading forms…</div>
      ) : (
        <div className="space-y-8">
          {CATEGORIES.map(cat => {
            const catTemplates = templates.filter(t => t.category === cat.id);
            const isUploading = uploadingCategory === cat.id;
            return (
              <section key={cat.id}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-medium text-gray-800">{cat.label}</h2>
                  <button
                    onClick={() => triggerUpload(cat.id)}
                    disabled={isUploading}
                    className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
                  >
                    {isUploading ? 'Uploading…' : '+ Upload'}
                  </button>
                </div>

                {catTemplates.length === 0 ? (
                  <div className="text-sm text-gray-500 border rounded-lg p-4">No forms uploaded yet</div>
                ) : (
                  <div className="space-y-2">
                    {catTemplates.map(t => (
                      <div key={t.id} className="flex items-center justify-between border rounded-lg p-3">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-800 truncate">{t.fileName}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatSize(t.size)} · {formatDate(t.uploadedAt)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          <button onClick={() => openTemplate(t.id, 'view')}
                            className="text-xs px-2.5 py-1.5 rounded-lg text-brand-600 hover:bg-brand-50 font-medium">
                            View
                          </button>
                          <button onClick={() => openTemplate(t.id, 'download')}
                            className="text-xs px-2.5 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 font-medium">
                            Download
                          </button>
                          <button
                            onClick={() => handleDelete(t.id)}
                            disabled={deletingId === t.id}
                            className="text-xs px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50 font-medium disabled:opacity-50"
                          >
                            {deletingId === t.id ? 'Removing…' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FormTemplates;
